/**
 * A dissipação do CPTu: a reta em √t (automática ou a do engenheiro, pelas alças), uᵢ, u₅₀, t₅₀,
 * Ir = G₀/Su, cₕ de Teh & Houlsby (1991), kₕ e o NA sugerido pela dissipação estabilizada — spec 07
 * §5.4 e §5.5; RN-252. Unidades do motor (`unidades.ts`): tempo em s, u em kPa, G₀ e M em MPa, Su em
 * kPa, cₕ em m²/s, kₕ em m/s, profundidade em m.
 *
 *   uᵢ  = a reta em √t = 0                      u₅₀ = (uᵢ + u₀)/2
 *   t₅₀ = (√t em que a reta vale u₅₀)²          Ir  = 1000·G₀/Su
 *   cₕ  = T*₅₀·r²·√Ir/t₅₀ (Teh & Houlsby 1991; T*₅₀ = 0,245 para u₂, r = raio do cone)
 *   kₕ  = cₕ·γw/(1000·M)                        cₕ por ano = cₕ·31.536.000
 *   NA  = z − u∞/γw, com a curva parada e o excesso acabado (sugestão, §5.4; escolha 4)
 *
 * Conferido contra as três dissipações do CPTu01 do gabarito 2609350 (CPeT-IT v3.9.7.4): com a reta
 * do CPeT-IT, o u₀ e o G₀ e o Su do registro, e o M implícito na tela, o t₅₀ sai igual (796,61;
 * 493,35; 96,86 s) e o Ir, o cₕ e o kₕ dentro de 0,5 % (`298.50`; a conformidade grava a conferência
 * em `CONFORMIDADE.dissipacoes`, e `dissipacaoHomologada` diz o que vai ao PDF e ao Portal). O cₕ e o
 * kₕ daqui são os da DISSIPAÇÃO (Teh & Houlsby); a coluna `ch` do perfil (k·M/γw, `correlacoes.ts`)
 * é outra grandeza, com outra homologação (`grandezaHomologada('ch')`). O que a fórmula publicada não
 * fixa saiu do gabarito (tarefa 311.6) e está aqui com a evidência; o que vem só do CPeT-IT vai
 * marcado «COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA», com a regra que o RT assina entre
 * colchetes — uma das `REGRAS_A_ASSINAR_DISSIPACAO` —, como em `classificacao.ts` e `correlacoes.ts`;
 * o registro velho do CPTu01 (escolha 3) vai marcado como o que é, um registro velho, não uma regra.
 *
 *  1. t₅₀ PELA RETA, SEMPRE — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR
 *     PELO RT [dis.t50-pela-reta]. A spec dizia «t₅₀ medido (interpolado na curva) ou extrapolado
 *     pela reta»; o CPeT-IT lê o t₅₀ na reta também quando a curva passa de u₅₀: a 10,02 m, 493,35 s
 *     pela reta, enquanto a curva medida cruza u₅₀ (319,68 kPa) aos 596,9 s (interpolada em √t entre 540 e 600 s); a
 *     14,60 m os dois quase coincidem (96,86 e 96,8 s). `t50Extrapolado` diz quando a curva medida
 *     NÃO chega a u₅₀ dentro do registro (6,50 m: termina em 180,44 kPa com u₅₀ = 162,48 kPa) — é o
 *     que o relatório escreve como «extrapolado pela reta». A curva conta a partir do pico do
 *     excesso (u − u₀ máximo), para que a subida inicial de um ensaio dilatante não pareça alcançar
 *     u₅₀.
 *  2. A RETA AUTOMÁTICA (`ajustarReta`): o trecho reto do MÁXIMO de u até o JOELHO da curva em √t.
 *     A curva, do máximo em diante, é reamostrada em 400 passos iguais de √t (interpolação linear
 *     entre as leituras) e dividida em dois trechos por regressão segmentada — o ponto de quebra que
 *     dá a menor soma dos quadrados dos resíduos das duas retas, cada trecho com pelo menos 10 % da
 *     faixa —; a reta é a regressão do primeiro trecho. A reamostragem tira o peso das leituras de
 *     1 em 1 s do começo do registro do Pagani (60 leituras no primeiro minuto, 12 nos 24 seguintes),
 *     que puxam qualquer regressão leitura a leitura para o primeiro minuto. Com o u₀ e o t₅₀ do
 *     CPeT-IT: 6,50 m 823,2 s (+3,3 %, r² 0,9960); 10,02 m 497,2 s (+0,8 %, r² 0,9922); 14,60 m
 *     104,9 s (a do CPeT-IT, ajustada à mão, dá 96,86; r² 0,9908). As retas do CPeT-IT de 6,50 e
 *     10,02 m não são a regressão de nenhum trecho contíguo de leituras (a mais próxima erra a
 *     inclinação e o uᵢ em 5,5×10⁻⁴ e 1,5×10⁻³), e nenhum critério testado as reproduz a 2 % ao
 *     mesmo tempo — elas puxam para lados opostos. Os outros critérios, com o t₅₀ a 6,50 / 10,02 m
 *     e a 14,60 m: o maior trecho a partir do máximo com r² ≥ 0,995 (o do plano) não existe a 6,50 m
 *     (o r² nunca passa de 0,9927), a 10,02 m para no patamar dos 5 primeiros segundos (+3.892 %) e
 *     a 14,60 m dá 238,4 s; com r² ≥ 0,99, −5,7 % / +23,2 % / 213,7 s; o trecho reto mais íngreme
 *     (r² ≥ 0,98, √t ≥ 8), −13,7 % / −13,6 % / 94,6 s; o maior trecho dentro de uma faixa de resíduo
 *     (RMS ≤ 0,8 % da queda, começando nos primeiros 15 % dela), −5,7 % / +4,3 % / 97,8 s; o maior
 *     trecho com r² ≥ 0,995 na curva reamostrada, +7,1 % / +27,8 % / 234,8 s; os dois trechos
 *     pulando a primeira leitura, +1,3 % / −2,3 % / 103,7 s. A reta automática é sugestão: o
 *     engenheiro a ajusta pelas alças (§5.5) e o relatório usa a dele.
 *  3. G₀, Su e M vêm de quem chama: «da própria vertical, ou digitado» (§5.5). Na própria vertical,
 *     `valoresNaProfundidade` dá a linha mais próxima da profundidade (ou a média numa janela), e o
 *     u₀ ali. Os registros do CPeT-IT do CPTu01 NÃO trazem a linha da interpretação (6,50 m: G₀
 *     12,874 MPa e Su 14,505 kPa no registro, 8,74 e 16,16 na linha; o M do kₕ, 0,690 MPa, contra
 *     1,14): eles são a linha da profundidade calculada com γ FIXO (o γ padrão do `.cpt`,
 *     19 kN/m³) e o NA NA SUPERFÍCIE (u₀ = γw·z) — `premissasDaDissipacaoDoCPeTIT` —, UM REGISTRO
 *     VELHO DO CPeT-IT, NÃO UMA REGRA DELE: O MOTOR SÓ O REPRODUZ PARA COMPARAR — A ASSINAR PELO RT
 *     [dis.premissas-do-cpetit] (o RT assina que o relatório não o usa). Com elas, nas três
 *     profundidades, o G₀ e o Su batem com o registro na precisão do float32 dele (diferença relativa
 *     ≤ 6×10⁻⁸), o M é o double gravado no registro (diferença ≤ 4,5×10⁻¹⁶) e o kₕ da conta inteira,
 *     com a reta do CPeT-IT, é o double do registro (≤ 1,1×10⁻⁷). Nada mais fechou: a linha da
 *     interpretação erra o G₀ em −27 a −32 %; médias em janelas de ±5 a ±50 cm, a leitura sem a média
 *     móvel, a leitura 1 a 3 cm acima ou abaixo, só o γ fixo ou só o NA na superfície erram pelo menos
 *     uma das três grandezas em 3 % ou mais (o `298.56` prende as duas pontas: a linha da
 *     interpretação não é o registro, e a linha com estas premissas é). O próprio registro explica
 *     por quê: ele guarda, num double depois do M, o NA com que foi calculado (o LEIA-ME do gabarito
 *     descreve o registro), e no CPTu01 esse NA é 0 — o registro foi gravado antes de o NA (0,86 m)
 *     ser informado. Nas outras verticais do `.cpt` (fora do gabarito, que só traz as curvas do
 *     CPTu01) o NA gravado é o da vertical, e 14 dos 25 registros são a linha da interpretação com γ
 *     automático e esse NA, na precisão do float32 do registro; o CPTu05 a 6,51 m (NA gravado 0) é
 *     γ fixo e NA 0, como o CPTu01; 10 registros não fecham com nenhuma das duas. Ou seja: o
 *     CPeT-IT grava a linha da vertical com as premissas do momento, e a do CPTu01 ficou velha. O
 *     relatório da Testesolo usa a própria vertical, com as premissas dela (spec §5.5);
 *     `premissasDaDissipacaoDoCPeTIT` reproduz o registro velho só para comparar.
 *  4. NA PELA DISSIPAÇÃO — escolha do motor, sem premissa: a sugestão só sai com as duas condições.
 *     · A curva parou: os u válidos dos últimos 20 % do TEMPO do registro (pelo menos três leituras;
 *       com menos, a janela recua até ter três) diferem menos de 1 % entre si — (máximo − mínimo)
 *       < 1 % de |u∞|, com u∞ a média deles. É o «menos de 1 %» da spec, julgado num trecho de tempo
 *       e não nas três últimas leituras: com uma leitura por segundo, as três últimas de uma curva que
 *       ainda cai diferem 0,01 % (`298.54`).
 *     · O excesso acabou: |u∞ − u₀|, com o u₀ hidrostático na profundidade e o NA das premissas
 *       (γw·(z − NA), ≥ 0), é no máximo 10 % do maior |u − u₀| do registro — a dissipação passou de
 *       90 %, o grau que o adensamento chama de praticamente completo (o t₉₀ de Taylor) — ou, se
 *       for maior, 1 m de coluna d'água (γw × 1 m): o solo drenado quase não gera excesso, e ali a
 *       razão mediria só o ruído. Uma curva parada longe do hidrostático é dissipação que ainda não
 *       terminou (o «menos de 1 %» de um u de 180 kPa aceita uma queda de 1,8 kPa) — ou um NA errado
 *       por mais que isso, que o motor não adivinha: a sugestão a menos é o custo da regra.
 *     O γw é o das premissas; NA = z − u∞/γw.
 *
 * Fora isso, a conta é a fórmula: o que não existe — Su ou G₀ ≤ 0 ou infinitos, t₅₀ ≤ 0, M ≤ 0 ou
 * infinito, u₀ que não é número, reta que não chega a u₅₀, curva sem leitura — é NaN, nunca Infinity
 * nem zero, e nada lança. O r² da reta automática fica em [0, 1] (o arredondamento de uma reta
 * perfeita dá 1,0000000000000004).
 */
import type { Premissas, RegraAAssinar, Resultado, Serie } from './tipos';

/** Uma reta da dissipação, por dois pontos em (√t [√s], u [kPa]). */
export interface Reta {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** O registro de uma dissipação: tempo (s) e poropressão u (kPa), na ordem do tempo. */
export interface Dissipacao {
  tempo: Serie;
  u: Serie;
}

/** A reta automática, as leituras em que ela começa e termina (índices de `tempo`) e o r². */
export interface RetaAjustada {
  reta: Reta;
  /** A leitura em que o trecho começa: a do máximo de u. NaN sem reta. */
  de: number;
  /** A última leitura do trecho, até o joelho (pelo menos a seguinte ao máximo). NaN sem reta. */
  ate: number;
  /** r² da regressão, sobre a curva reamostrada em √t (escolha 2). NaN sem reta. */
  r2: number;
  /**
   * Por que a reta automática não serve, para a tela dizer ao engenheiro (que traça a dele pelas
   * alças): sem reta (NaN), o que faltou — nenhuma leitura válida, uma leitura só, um registro que
   * só sobe —; com uma reta que não desce, que não há dissipação a interpretar nela. null quando a
   * reta existe e desce.
   */
  motivo: string | null;
}

/** A interpretação de uma dissipação. */
export interface InterpretacaoDaDissipacao {
  /** Poropressão inicial, kPa: a reta em √t = 0. */
  ui: number;
  /** Poropressão de equilíbrio, kPa: a que entrou. */
  u0: number;
  /** (uᵢ + u₀)/2, kPa. */
  u50: number;
  /** Tempo de 50 % de dissipação, s, pela reta (escolha 1). Homologação: `dissipacaoHomologada('t50')`. */
  t50: number;
  /** A curva medida não chega a u₅₀ dentro do registro: o t₅₀ é extrapolado pela reta. */
  t50Extrapolado: boolean;
  /** Índice de rigidez, 1000·G₀/Su. Homologação: `dissipacaoHomologada('ir')`. */
  ir: number;
  /**
   * Coeficiente de adensamento horizontal DA DISSIPAÇÃO (Teh & Houlsby 1991), m²/s — não é a
   * coluna `ch` do perfil (k·M/γw). Homologação: `dissipacaoHomologada('ch')`, não
   * `grandezaHomologada('ch')`.
   */
  ch: number;
  /** cₕ da dissipação em m²/ano (o mesmo cₕ, noutra unidade: vale a homologação do `ch`). */
  chAno: number;
  /** Permeabilidade horizontal da dissipação, cₕ·γw/M, m/s. Homologação: `dissipacaoHomologada('kh')`. */
  kh: number;
  /** A reta usada. */
  reta: Reta;
  /** A reta é a automática (`ajustarReta`), não a que entrou. */
  retaAutomatica: boolean;
  /** NA sugerido pela dissipação, m; null se a curva não parou ou o excesso não acabou (escolha 4). */
  naEstimado: number | null;
}

/** Os passos iguais de √t em que a curva é reamostrada para a reta automática (escolha 2). */
const PASSOS_DA_REAMOSTRA = 400;

/** A fração mínima da faixa de √t de cada um dos dois trechos da regressão segmentada (escolha 2). */
const FRACAO_MINIMA_DO_TRECHO = 0.1;

/** Segundos num ano de 365 dias (cₕ por ano). */
const SEGUNDOS_POR_ANO = 31536000;

/** A fração final do tempo do registro em que a curva tem de estar parada para sugerir o NA (escolha 4). */
const FRACAO_FINAL_DO_TEMPO = 0.2;

/** Os u dessa janela diferem menos que isto, em fração de u∞ (o «menos de 1 %» da spec; escolha 4). */
const ESTABILIZACAO = 0.01;

/** Pelo menos estas leituras na janela da estabilização; com menos, ela recua até ter (escolha 4). */
const LEITURAS_DA_ESTABILIZACAO = 3;

/** O excesso que sobra no fim pode ser até esta fração do maior excesso do registro (escolha 4)… */
const EXCESSO_RESIDUAL_MAXIMO = 0.1;

/** …ou, se for maior, esta coluna d'água, em m (o solo drenado quase não gera excesso; escolha 4). */
const EXCESSO_RESIDUAL_PISO_M = 1;

/** Folga de ponto flutuante nas comparações de profundidade e de √t. */
const FOLGA = 1e-9;

/** O número, ou NaN quando ele é infinito ou não existe; o −0 da aritmética sai 0 (como no básico). */
const finito = (x: number): number => (Number.isFinite(x) ? (x === 0 ? 0 : x) : NaN);

/** Positivo e finito (um G₀, Su ou M infinito não é número: daria Ir ou kₕ zero). */
const positivo = (x: number): boolean => Number.isFinite(x) && x > 0;

const RETA_VAZIA: Reta = Object.freeze({ x1: NaN, y1: NaN, x2: NaN, y2: NaN });

/** Os motivos de `ajustarReta` quando a reta automática não serve (a tela os mostra ao engenheiro). */
const MOTIVO_SEM_LEITURA = 'nenhuma leitura com tempo e u válidos: não há curva para ajustar a reta';
const MOTIVO_UMA_LEITURA = 'uma leitura válida só: não há curva para ajustar a reta';
const MOTIVO_SO_SOBE =
  'o registro só sobe: o maior u é a última leitura, e não há queda depois dele para ajustar a reta';
const MOTIVO_SEM_REGRESSAO = 'a regressão da queda não tem solução';
const MOTIVO_NAO_DESCE =
  'a curva não cai depois do máximo: a reta não desce, e não há dissipação a interpretar nela';

/** Regressão de y em x (mínimos quadrados) de y[i..j], com o r²; NaN onde não existe. */
function regressao(x: number[], y: number[], i: number, j: number): { a: number; b: number; r2: number } {
  const n = j - i + 1;
  let mx = 0;
  let my = 0;
  for (let k = i; k <= j; k++) {
    mx += x[k];
    my += y[k];
  }
  mx /= n;
  my /= n;
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (let k = i; k <= j; k++) {
    const dx = x[k] - mx;
    const dy = y[k] - my;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  const b = finito(sxy / sxx);
  const a = finito(my - b * mx);
  // O arredondamento de uma reta perfeita dá r² = 1,0000000000000004: o r² fica em [0, 1] (NaN continua NaN).
  return { a, b, r2: Math.min(1, Math.max(0, finito((sxy * sxy) / (sxx * syy)))) };
}

/**
 * Somas acumuladas de x, y, x², xy e y², para a soma dos quadrados dos resíduos de qualquer trecho
 * em tempo constante (a busca do joelho percorre todos os pontos de quebra).
 */
function somasAcumuladas(x: number[], y: number[]): (i: number, j: number) => number {
  const n = x.length;
  const sx = new Float64Array(n + 1);
  const sy = new Float64Array(n + 1);
  const sxx = new Float64Array(n + 1);
  const sxy = new Float64Array(n + 1);
  const syy = new Float64Array(n + 1);
  for (let k = 0; k < n; k++) {
    sx[k + 1] = sx[k] + x[k];
    sy[k + 1] = sy[k] + y[k];
    sxx[k + 1] = sxx[k] + x[k] * x[k];
    sxy[k + 1] = sxy[k] + x[k] * y[k];
    syy[k + 1] = syy[k] + y[k] * y[k];
  }
  return (i, j) => {
    const m = j - i + 1;
    const Sx = sx[j + 1] - sx[i];
    const Sy = sy[j + 1] - sy[i];
    const Sxx = sxx[j + 1] - sxx[i] - (Sx * Sx) / m;
    const Sxy = sxy[j + 1] - sxy[i] - (Sx * Sy) / m;
    const Syy = syy[j + 1] - syy[i] - (Sy * Sy) / m;
    return Sxx > 0 ? Math.max(0, Syy - (Sxy * Sxy) / Sxx) : Math.max(0, Syy);
  };
}

/**
 * A reta automática da dissipação (escolha 2 do cabeçalho): do máximo de u até o joelho da curva
 * em √t, achado por regressão segmentada em dois trechos sobre a curva reamostrada em passos iguais
 * de √t. Leituras sem número (ou com o tempo negativo ou fora de ordem) ficam de fora. Sem duas
 * leituras do máximo em diante, a reta, o trecho e o r² são NaN, e `motivo` diz o que faltou; com
 * uma reta que não desce, `motivo` diz que não há dissipação nela. Nunca lança.
 */
export function ajustarReta(d: Dissipacao): RetaAjustada {
  const nada = (motivo: string): RetaAjustada => ({ reta: { ...RETA_VAZIA }, de: NaN, ate: NaN, r2: NaN, motivo });
  const raiz: number[] = [];
  const u: number[] = [];
  const indice: number[] = [];
  const total = Math.min(d.tempo.length, d.u.length);
  for (let i = 0; i < total; i++) {
    const t = d.tempo[i];
    const v = d.u[i];
    if (!(t >= 0) || !Number.isFinite(t) || !Number.isFinite(v)) continue;
    const r = Math.sqrt(t);
    if (raiz.length > 0 && r <= raiz[raiz.length - 1]) continue; // o tempo tem de crescer
    raiz.push(r);
    u.push(v);
    indice.push(i);
  }
  if (u.length === 0) return nada(MOTIVO_SEM_LEITURA);
  if (u.length === 1) return nada(MOTIVO_UMA_LEITURA);
  let iMax = 0;
  for (let i = 1; i < u.length; i++) if (u[i] > u[iMax]) iMax = i;
  if (u.length - iMax < 2) return nada(MOTIVO_SO_SOBE);

  // A curva do máximo em diante, reamostrada em passos iguais de √t.
  const x0 = raiz[iMax];
  const passo = (raiz[raiz.length - 1] - x0) / PASSOS_DA_REAMOSTRA;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let m = 0, k = iMax; m <= PASSOS_DA_REAMOSTRA; m++) {
    const x = m === PASSOS_DA_REAMOSTRA ? raiz[raiz.length - 1] : x0 + m * passo;
    while (k < raiz.length - 2 && raiz[k + 1] < x) k++;
    const f = (x - raiz[k]) / (raiz[k + 1] - raiz[k]);
    xs.push(x);
    ys.push(u[k] + f * (u[k + 1] - u[k]));
  }

  // O joelho: o ponto de quebra que dá a menor soma dos quadrados dos resíduos dos dois trechos.
  const n = xs.length;
  const minimo = Math.max(3, Math.ceil(FRACAO_MINIMA_DO_TRECHO * n));
  const sse = somasAcumuladas(xs, ys);
  let quebra = n; // sem lugar para dois trechos, um só
  let menor = Infinity;
  for (let p = minimo; p <= n - minimo; p++) {
    const s = sse(0, p - 1) + sse(p, n - 1);
    if (s < menor) {
      menor = s;
      quebra = p;
    }
  }
  const fim = quebra - 1;
  const r = regressao(xs, ys, 0, fim);
  if (Number.isNaN(r.b)) return nada(MOTIVO_SEM_REGRESSAO);

  let ate = iMax;
  while (ate + 1 < raiz.length && raiz[ate + 1] <= xs[fim] + FOLGA) ate++;
  if (ate === iMax) ate = iMax + 1;
  return {
    reta: { x1: x0, y1: r.a + r.b * x0, x2: xs[fim], y2: r.a + r.b * xs[fim] },
    de: indice[iMax],
    ate: indice[ate],
    r2: r.r2,
    motivo: r.b < 0 ? null : MOTIVO_NAO_DESCE,
  };
}

/**
 * A interpretação de uma dissipação (spec 07 §5.5): com a reta dada (as alças do engenheiro, ou a
 * do CPeT-IT) ou, sem ela, a automática; o u₀ é o que entrou (hidrostático na cota ou digitado); o
 * G₀ (MPa), o Su (kPa) e o M (MPa) também (`valoresNaProfundidade`, ou digitados); das premissas
 * vêm γw, T*₅₀ e o raio do cone. Nunca lança: o que não existe é NaN.
 */
export function interpretarDissipacao(
  d: Dissipacao,
  x: {
    profundidade: number;
    u0: number;
    reta?: Reta | null;
    g0MPa: number;
    suKPa: number;
    mMPa: number;
    premissas: Premissas;
  },
): InterpretacaoDaDissipacao {
  const p = x.premissas;
  const retaAutomatica = !x.reta;
  const reta: Reta = x.reta ? { x1: x.reta.x1, y1: x.reta.y1, x2: x.reta.x2, y2: x.reta.y2 } : ajustarReta(d).reta;

  const inclinacao = finito((reta.y2 - reta.y1) / (reta.x2 - reta.x1));
  const ui = finito(reta.y1 - inclinacao * reta.x1);
  const u0 = finito(x.u0);
  const u50 = finito((ui + u0) / 2);
  const raizT50 = finito((u50 - ui) / inclinacao);
  // A reta só vale para √t ≥ 0, e t₅₀ = 0 (uᵢ = u₀: não há excesso a dissipar) não é tempo de dissipação.
  const t50 = raizT50 > 0 ? raizT50 * raizT50 : NaN;

  // A curva medida chega a u₅₀? Do pico do excesso em diante (escolha 1).
  let t50Extrapolado = false;
  if (Number.isFinite(t50)) {
    const sinal = ui >= u0 ? 1 : -1;
    const n = Math.min(d.tempo.length, d.u.length);
    let pico = -1;
    for (let i = 0; i < n; i++) {
      if (Number.isFinite(d.u[i]) && (pico < 0 || (d.u[i] - u0) * sinal > (d.u[pico] - u0) * sinal)) pico = i;
    }
    let alcancou = false;
    for (let i = Math.max(pico, 0); i < n && !alcancou; i++) {
      if (Number.isFinite(d.u[i]) && (d.u[i] - u50) * sinal <= 0) alcancou = true;
    }
    t50Extrapolado = !alcancou;
  }

  const ir = positivo(x.g0MPa) && positivo(x.suKPa) ? finito((1000 * x.g0MPa) / x.suKPa) : NaN;
  const ch = t50 > 0 ? finito((p.t50Fator * p.raioCone * p.raioCone * Math.sqrt(ir)) / t50) : NaN;
  const kh = positivo(x.mMPa) ? finito((ch * p.gamaW) / (1000 * x.mMPa)) : NaN;

  return {
    ui,
    u0,
    u50,
    t50,
    t50Extrapolado,
    ir,
    ch,
    chAno: finito(ch * SEGUNDOS_POR_ANO),
    kh,
    reta,
    retaAutomatica,
    naEstimado: naPelaDissipacao(d, x.profundidade, p),
  };
}

/**
 * NA = z − u∞/γw quando a curva parou nos últimos 20 % do tempo do registro e o excesso que sobra é
 * pequeno perto do u₀ hidrostático com o NA das premissas; senão null (escolha 4 do cabeçalho). As
 * leituras são as do registro, na ordem do tempo; as que não são número ficam de fora.
 */
function naPelaDissipacao(d: Dissipacao, z: number, p: Premissas): number | null {
  const gamaW = p.gamaW;
  if (!Number.isFinite(z) || !positivo(gamaW) || !Number.isFinite(p.na)) return null;
  const t: number[] = [];
  const u: number[] = [];
  const n = Math.min(d.tempo.length, d.u.length);
  for (let i = 0; i < n; i++) {
    if (Number.isFinite(d.tempo[i]) && Number.isFinite(d.u[i])) {
      t.push(d.tempo[i]);
      u.push(d.u[i]);
    }
  }
  if (u.length < LEITURAS_DA_ESTABILIZACAO) return null;

  // A janela: as leituras dos últimos 20 % do tempo, e pelo menos as três últimas.
  const tFim = t[t.length - 1];
  const inicio = tFim - FRACAO_FINAL_DO_TEMPO * (tFim - t[0]);
  let k = u.length - 1;
  while (k > 0 && (t[k - 1] >= inicio || u.length - k < LEITURAS_DA_ESTABILIZACAO)) k--;
  let soma = 0;
  let minimo = Infinity;
  let maximo = -Infinity;
  for (let i = k; i < u.length; i++) {
    soma += u[i];
    if (u[i] < minimo) minimo = u[i];
    if (u[i] > maximo) maximo = u[i];
  }
  const uInfinito = soma / (u.length - k);
  if (!(maximo - minimo < ESTABILIZACAO * Math.abs(uInfinito))) return null; // a curva não parou

  // O excesso acabou: o que sobra, perto do maior excesso do registro (ou de 1 m de água).
  const u0 = gamaW * Math.max(0, z - p.na);
  let maiorExcesso = 0;
  for (const v of u) maiorExcesso = Math.max(maiorExcesso, Math.abs(v - u0));
  const tolerado = Math.max(EXCESSO_RESIDUAL_MAXIMO * maiorExcesso, gamaW * EXCESSO_RESIDUAL_PISO_M);
  if (!(Math.abs(uInfinito - u0) <= tolerado)) return null;

  const na = finito(z - uInfinito / gamaW);
  return Number.isNaN(na) ? null : na;
}

/**
 * u₀ (kPa) em z: a interpolação linear da coluna u₀ entre a leitura logo acima e a logo abaixo de z
 * (a da própria profundidade, se houver). u₀ é função da profundidade, não propriedade do solo: não
 * se tira média dele numa janela, e no perfil hidrostático a interpolação é exata. NaN sem a coluna
 * ou sem leitura com u₀.
 */
function u0NaProfundidade(zs: Serie, u0: Serie | undefined, z: number): number {
  if (!u0) return NaN;
  let acima = -1;
  let abaixo = -1;
  for (let i = 0; i < zs.length; i++) {
    if (!Number.isFinite(zs[i]) || !Number.isFinite(u0[i])) continue;
    if (zs[i] <= z + FOLGA && (acima < 0 || zs[i] > zs[acima])) acima = i;
    if (zs[i] >= z - FOLGA && (abaixo < 0 || zs[i] < zs[abaixo])) abaixo = i;
  }
  if (acima < 0 || abaixo < 0) return acima < 0 && abaixo < 0 ? NaN : finito(u0[Math.max(acima, abaixo)]);
  if (zs[abaixo] - zs[acima] <= FOLGA) return finito(u0[acima]);
  return finito(u0[acima] + ((u0[abaixo] - u0[acima]) * (z - zs[acima])) / (zs[abaixo] - zs[acima]));
}

/**
 * G₀ (MPa), Su (kPa) e M (MPa) de uma vertical interpretada na profundidade da dissipação: os da
 * leitura mais próxima de `z` ou, com `janela` (largura em m, centrada em z), a média das leituras
 * a até meia janela, cada grandeza sem as leituras em que ela não se aplica. E o u₀ (kPa) em z,
 * interpolado na coluna u₀ (com ou sem janela), para o u₀ da dissipação «hidrostático na cota»
 * (§5.5). Fora da faixa de profundidade da vertical, ou sem leitura na janela, NaN (o engenheiro
 * digita).
 *
 * São os valores da interpretação que entrou, com as premissas dela — os que o relatório usa. Os do
 * registro velho do CPeT-IT saem da interpretação com `premissasDaDissipacaoDoCPeTIT`, só para
 * comparar (escolha 3 do cabeçalho).
 */
export function valoresNaProfundidade(
  r: Resultado,
  z: number,
  janela?: number,
): { g0MPa: number; suKPa: number; mMPa: number; u0KPa: number } {
  const nada = { g0MPa: NaN, suKPa: NaN, mMPa: NaN, u0KPa: NaN };
  const zs = r.col.z;
  const colunaU0: Serie | undefined = r.col.u0;
  if (!Number.isFinite(z) || zs.length === 0) return nada;
  let zMin = Infinity;
  let zMax = -Infinity;
  for (let i = 0; i < zs.length; i++) {
    if (zs[i] < zMin) zMin = zs[i];
    if (zs[i] > zMax) zMax = zs[i];
  }
  if (z < zMin - FOLGA || z > zMax + FOLGA) return nada;

  const colunas = [r.col.g0, r.col.su, r.col.m];
  if (janela !== undefined && Number.isFinite(janela) && janela > 0) {
    const media = (s: Serie): number => {
      let soma = 0;
      let quantos = 0;
      for (let i = 0; i < zs.length; i++) {
        if (Math.abs(zs[i] - z) <= janela / 2 + FOLGA && Number.isFinite(s[i])) {
          soma += s[i];
          quantos++;
        }
      }
      return quantos > 0 ? soma / quantos : NaN;
    };
    const [g0MPa, suKPa, mMPa] = colunas.map(media);
    return { g0MPa, suKPa, mMPa, u0KPa: u0NaProfundidade(zs, colunaU0, z) };
  }
  let maisPerto = -1;
  for (let i = 0; i < zs.length; i++) {
    if (Number.isFinite(zs[i]) && (maisPerto < 0 || Math.abs(zs[i] - z) < Math.abs(zs[maisPerto] - z))) maisPerto = i;
  }
  if (maisPerto < 0) return nada;
  const [g0MPa, suKPa, mMPa] = colunas.map((s) => finito(s[maisPerto]));
  return { g0MPa, suKPa, mMPa, u0KPa: u0NaProfundidade(zs, colunaU0, z) };
}

/**
 * O que da dissipação o RT assina (escolhas 1 e 3 do cabeçalho): o t₅₀ pela reta, copiado do
 * CPeT-IT, e o registro velho do CPTu01, que o motor reproduz só para comparar e o relatório não usa.
 * Um por rótulo do comentário, com o mesmo id entre colchetes: a conformidade (`conformidade.ts`) os
 * leva ao RT para assinar, e o `298.64` confere que a lista e os rótulos são os mesmos. A reta
 * automática (escolha 2) e o NA sugerido (escolha 4) são sugestões que o engenheiro confirma, não
 * regras do resultado.
 */
export const REGRAS_A_ASSINAR_DISSIPACAO: readonly RegraAAssinar[] = Object.freeze(
  [
    {
      id: 'dis.t50-pela-reta',
      arquivo: 'dissipacao.ts',
      descricao: 'O t₅₀ é lido sempre na reta em √t, também quando a curva medida passa de u₅₀ dentro do registro '
        + '(a spec dizia «medido na curva ou extrapolado pela reta»).',
      evidencia: 'Com a reta do CPeT-IT, o t₅₀ das três dissipações do CPTu01 sai o dele (796,61; 493,35; 96,86 s); '
        + 'a 10,02 m a curva medida cruza u₅₀ aos 596,9 s e o CPeT-IT dá 493,35 s, o da reta.',
    },
    {
      id: 'dis.premissas-do-cpetit',
      arquivo: 'dissipacao.ts',
      descricao: 'O G₀, o Su e o M dos registros de dissipação do CPTu01 no gabarito não são os da vertical: são um '
        + 'registro velho, a linha calculada com γ fixo (19 kN/m³) e NA 0, as premissas de quando os registros '
        + 'foram gravados, antes de o NA (0,86 m) ser informado — não uma regra do CPeT-IT. O relatório da '
        + 'Testesolo usa os da própria vertical, com as premissas dela (spec §5.5); o motor só reproduz o registro '
        + 'velho para comparar (premissasDaDissipacaoDoCPeTIT), e o Ir, o cₕ e o kₕ do CPeT-IT para o CPTu01 não '
        + 'servem de referência.',
      evidencia: 'Com essas premissas, G₀ e Su batem com o registro na precisão do float32 (≤ 6×10⁻⁸) e o M é o '
        + 'double do registro, que guarda o NA usado: 0 no CPTu01. Nas outras verticais do .cpt, 14 dos 25 '
        + 'registros são a linha da vertical com γ automático e o NA gravado, na precisão do float32 do registro; '
        + '1 (CPTu05 a 6,51 m, NA gravado 0) é γ fixo e NA 0, como o CPTu01; e 10 não fecham com nenhuma das duas. '
        + 'Com as premissas da vertical, o Ir do CPTu01 sai 30 a 39 % menor e o kₕ 20 a 53 % menor (298.56).',
    },
  ].map((r) => Object.freeze(r)),
);

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: o relatório usa o G₀, o Su e o M da própria vertical,
 * com as premissas dela (spec §5.5).
 *
 * As premissas com que os registros de dissipação do CPTu01 no gabarito foram calculados: as dadas,
 * com o γ FIXO (o `gamaFixo`, o γ padrão do `.cpt`) e o NA NA SUPERFÍCIE (u₀ hidrostático desde
 * z = 0) — UM REGISTRO VELHO DO CPeT-IT, NÃO UMA REGRA DELE: O MOTOR SÓ O REPRODUZ PARA COMPARAR — A
 * ASSINAR PELO RT [dis.premissas-do-cpetit] (escolha 3 do cabeçalho; `298.56`). Devolve um objeto
 * novo; as de entrada não mudam.
 */
export function premissasDaDissipacaoDoCPeTIT(p: Premissas): Premissas {
  const pontos = Array.isArray(p.u0Pontos) ? p.u0Pontos.map((q) => ({ z: q.z, u0: q.u0 })) : [];
  return { ...p, gamaModo: 'fixo', na: 0, u0Modo: 'hidrostatico', u0Pontos: pontos };
}
